"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Crown, Leaf } from "lucide-react";

type Product = {
  name: string;
  desc: string;
  price: number;
  unit: string;
  image: string;
  tag?: string;
};

type Range = {
  id: string;
  name: string;
  subtitle: string;
  image: string;
  color: string;
  icon: typeof Crown;
  intro: string;
  products: Product[];
};

const ranges: Range[] = [
  {
    id: "patate",
    name: "PATATE OR",
    subtitle: "Gamme Patate Douce",
    image: "/images/gamme-patate.png",
    color: "from-orange-500/20 to-amber-400/20",
    icon: Crown,
    intro:
      "La patate douce dorée, reine de nos champs, déclinée en six produits transformés d'exception. De la racine fraîche au sirop précieux, toute la richesse de ce trésor nutritionnel.",
    products: [
      { name: "Patate Douce Fraîche", desc: "Racine lavée, calibre premium, peau ambrée.", price: 1200, unit: "kg", image: "/images/patate-douce.png", tag: "Best-seller" },
      { name: "Farine de Patate Douce", desc: "Farine fine sans gluten, riche en fibres.", price: 2500, unit: "kg", image: "/images/gamme-patate.png" },
      { name: "Chips de Patate Douce", desc: "Tranches fines, frites à l'huile de palme.", price: 1500, unit: "sachet 250g", image: "/images/chips-patate.png", tag: "Artisanal" },
      { name: "Purée de Patate Douce", desc: "Purée prête à réchauffer, rien d'ajouté.", price: 1800, unit: "pot 500g", image: "/images/gamme-patate.png" },
      { name: "Patate Douce Séchée", desc: "Tranches déshydratées, longue conservation.", price: 2200, unit: "sachet 500g", image: "/images/gamme-patate.png" },
      { name: "Sirop de Patate Douce", desc: "Sirop naturel, alternative au sucre raffiné.", price: 3500, unit: "flacon 50cl", image: "/images/gamme-patate.png", tag: "Premium" },
    ],
  },
  {
    id: "pdt",
    name: "POMME DE TERRE OR",
    subtitle: "Gamme Pomme de Terre",
    image: "/images/gamme-pdt.png",
    color: "from-yellow-400/20 to-amber-500/20",
    icon: Sparkles,
    intro:
      "La pomme de terre universelle, racine familière élevée au rang d'or. Six transformations qui allient tradition paysanne et raffinement moderne.",
    products: [
      { name: "Pomme de Terre Fraîche", desc: "Calibre homogène, peau fine dorée.", price: 900, unit: "kg", image: "/images/pomme-de-terre.png" },
      { name: "Farine de Pomme de Terre", desc: "Farine blanche, idéale pâtisserie.", price: 2800, unit: "kg", image: "/images/gamme-pdt.png" },
      { name: "Flocons de Purée", desc: "Flocons déshydratés, prêts en 3 minutes.", price: 2000, unit: "boîte 400g", image: "/images/gamme-pdt.png" },
      { name: "Chips de Pomme de Terre", desc: "Fraîchement coupées, sel de mer.", price: 1400, unit: "sachet 250g", image: "/images/chips-patate.png" },
      { name: "Pomme de Terre Précuite", desc: "Vapeur, prête en 5 min à la poêle.", price: 1600, unit: "sachet 1kg", image: "/images/gamme-pdt.png" },
      { name: "Vodka de Pomme de Terre", desc: "Spiritueux artisanal, triple distillation.", price: 8000, unit: "flacon 70cl", image: "/images/gamme-pdt.png", tag: "Édition limitée" },
    ],
  },
  {
    id: "manioc",
    name: "MANIOC OR",
    subtitle: "Gamme Manioc",
    image: "/images/gamme-manioc.png",
    color: "from-emerald-400/20 to-green-600/20",
    icon: Leaf,
    intro:
      "Le manioc, pilier nourricier de l'Afrique, décliné en six trésors transformés. La fermentation, le séchage, la cuisson : un savoir-faire ancestral sublimé.",
    products: [
      { name: "Manioc Frais", desc: "Racine récoltée du jour, chair blanche.", price: 600, unit: "kg", image: "/images/manioc.png" },
      { name: "Farine de Manioc", desc: "Fermentation naturelle, séchage solaire.", price: 2500, unit: "kg", image: "/images/farine-manioc.png", tag: "Sans gluten" },
      { name: "Attiéké", desc: "Couscous de manioc fermenté, prêt à déguster.", price: 1800, unit: "kg", image: "/images/attieke.png", tag: "Recette héritée" },
      { name: "Placali", desc: "Pâte de manioc fermentée, onctueuse.", price: 1500, unit: "pot 500g", image: "/images/gamme-manioc.png" },
      { name: "Gari", desc: "Semoule grillée, snack ou accompagnement.", price: 2000, unit: "sachet 1kg", image: "/images/gamme-manioc.png" },
      { name: "Tapioca", desc: "Perles de manioc, desserts et potages.", price: 3200, unit: "sachet 500g", image: "/images/gamme-manioc.png", tag: "Premium" },
    ],
  },
  {
    id: "igname",
    name: "IGNAME OR",
    subtitle: "Gamme Igname",
    image: "/images/gamme-igname.png",
    color: "from-amber-500/20 to-orange-600/20",
    icon: Crown,
    intro:
      "L'igname royal, tubercule noble des tropiques, magnifié en six transformations rares. Du foutou traditionnel aux chips croustillantes : un festin.",
    products: [
      { name: "Igname Frais", desc: "Tubercule calibre royal, chair crème.", price: 1000, unit: "kg", image: "/images/igname.png", tag: "Édition noble" },
      { name: "Farine d'Igname", desc: "Farine fine, base du foutou moderne.", price: 3000, unit: "kg", image: "/images/gamme-igname.png" },
      { name: "Cossettes d'Igname", desc: "Morceaux séchés, longue conservation.", price: 2500, unit: "sachet 500g", image: "/images/gamme-igname.png" },
      { name: "Foutou Mix", desc: "Mélange prêt à préparer, igname + banane.", price: 2200, unit: "sachet 1kg", image: "/images/gamme-igname.png" },
      { name: "Igname Précuit", desc: "Cubes vapeur, prêts en 5 minutes.", price: 1800, unit: "sachet 1kg", image: "/images/gamme-igname.png" },
      { name: "Chips d'Igname", desc: "Tranches fines, sel de Guérande.", price: 1700, unit: "sachet 250g", image: "/images/chips-patate.png", tag: "Artisanal" },
    ],
  },
];

export function Gammes() {
  const [tab, setTab] = useState("patate");
  const active = ranges.find((r) => r.id === tab)!;

  return (
    <section id="gammes" className="relative py-24 lg:py-32 bg-gradient-to-b from-background to-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Nos Gammes
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Quatre <span className="text-gradient-gold">cultures</span>, quatre{" "}
            <span className="text-gradient-gold">gammes d&apos;or</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Chaque culture possède sa propre signature, sa propre famille de produits
            transformés. PATATE OR, POMME DE TERRE OR, MANIOC OR, IGNAME OR : quatre
            gammes d&apos;exception, vingt-quatre produits nobles.
          </motion.p>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12"
        >
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="flex justify-center">
              <TabsList className="grid w-full max-w-2xl grid-cols-2 sm:grid-cols-4 bg-secondary/60 rounded-2xl p-1.5 gap-1">
                {ranges.map((r) => (
                  <TabsTrigger
                    key={r.id}
                    value={r.id}
                    className="rounded-xl data-[state=active]:bg-gradient-gold data-[state=active]:text-white data-[state=active]:shadow-gold transition-all text-xs sm:text-sm font-semibold"
                  >
                    {r.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Range content */}
            {ranges.map((r) => (
              <TabsContent key={r.id} value={r.id} className="mt-10">
                <div className="grid lg:grid-cols-12 gap-8 items-start">
                  {/* Range showcase */}
                  <div className="lg:col-span-5 lg:sticky lg:top-28">
                    <div className="relative rounded-3xl overflow-hidden shadow-gold-lg ring-1 ring-gold/30">
                      <img
                        src={r.image}
                        alt={`Gamme ${r.name}`}
                        className="w-full h-80 lg:h-96 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/90 via-earth-deep/20 to-transparent" />

                      <div className="absolute top-4 left-4">
                        <Badge className="bg-gradient-gold text-white shadow-gold border-0">
                          <r.icon className="w-3 h-3 mr-1" />
                          Gamme Complète
                        </Badge>
                      </div>

                      <div className="absolute bottom-5 left-5 right-5">
                        <div className="text-[10px] uppercase tracking-[0.32em] text-gold-soft font-semibold">
                          {r.subtitle}
                        </div>
                        <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-cream mt-1">
                          {r.name}
                        </h3>
                        <p className="mt-3 text-sm text-cream/85 leading-relaxed">
                          {r.intro}
                        </p>
                        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream/15 backdrop-blur text-cream text-xs font-semibold">
                          <Sparkles className="w-3.5 h-3.5 text-gold" />
                          {r.products.length} produits · Vente directe
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products grid */}
                  <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                    {r.products.map((p, i) => (
                      <motion.div
                        key={p.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        className="group relative p-4 rounded-2xl bg-card border border-border hover:border-gold hover:shadow-gold transition-all duration-500 flex gap-3"
                      >
                        <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden ring-1 ring-gold/20">
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-display text-sm font-bold text-foreground leading-tight">
                              {p.name}
                            </h4>
                            {p.tag && (
                              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-gradient-gold text-white text-[9px] font-bold uppercase tracking-wide">
                                {p.tag}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground leading-snug line-clamp-2">
                            {p.desc}
                          </p>
                          <div className="mt-2 flex items-center justify-between">
                            <div>
                              <span className="font-display text-sm font-bold text-gradient-gold">
                                {p.price.toLocaleString("fr-FR")}
                              </span>
                              <span className="text-xs text-muted-foreground ml-1">
                                FCFA / {p.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}
